import { FC } from 'react';
import Container from '@amzn/awsui-components-core/container';
import Header from '@amzn/awsui-components-core/header';
import FormField from '@amzn/awsui-components-core/form-field';
import Input from '@amzn/awsui-components-core/input';
import { useTranslate } from 'src/hooks/useTranslate';
import { SectionBaseProps } from '../../types';

export interface NcemiPriceLeadingSectionProps extends SectionBaseProps {
    /** Current price leading guardrail value (min 3%) */
    priceLeadingGuardrail: string;
    /** Callback to update the price leading guardrail value */
    setPriceLeadingGuardrail: (value: string) => void;
    /** Inline validation error message */
    errorText?: string;
}

/**
 * NCEMI price leading guardrail section — renders a single numeric input
 * for the price leading guardrail percentage (minimum 3%).
 * Only shown for NO_COST_EMI campaign type (parent controls visibility).
 * Owns its own i18n bundle (`organisms.ncemiPriceLeadingSection`) so translations
 * are local to this component rather than prop-drilled from the page.
 */
const NcemiPriceLeadingSection: FC<NcemiPriceLeadingSectionProps> = ({
    priceLeadingGuardrail,
    setPriceLeadingGuardrail,
    mode,
    disabled,
    errorText,
}) => {
    const { translate } = useTranslate('organisms.ncemiPriceLeadingSection');
    const isDisabled = disabled ?? mode === 'view';

    return (
        <Container
            header={
                <Header variant="h2">{translate('section_price_leading_guardrail')}</Header>
            }
        >
            <FormField
                label={translate('price_leading_guardrail_label')}
                description={translate('price_leading_guardrail_description')}
                errorText={errorText}
                i18nStrings={{ errorIconAriaLabel: translate('error_icon_aria_label') }}
            >
                <Input
                    value={priceLeadingGuardrail}
                    onChange={({ detail }) => setPriceLeadingGuardrail(detail.value)}
                    type="number"
                    placeholder="3"
                    ariaRequired={true}
                    disabled={isDisabled}
                    data-testid="amazon-price-leading-guardrail"
                    invalid={!!errorText}
                />
            </FormField>
        </Container>
    );
};

export default NcemiPriceLeadingSection;
